export const SOFT_SKIP_DWELL_MS = 2000;
export const DECK_STOP_SWIPES = 50;
/** First wait after a page fetch that failed (null: timeout, 429, a shed 503);
 * doubles per consecutive failure up to DECK_RETRY_MAX_MS. */
export const DECK_RETRY_BASE_MS = 2000;
export const DECK_RETRY_MAX_MS = 30000;

interface Visit {
  arrivedAt: number;
  leftForwardAfter: number | null;
  returned: boolean;
}

export class Deck<T extends { id: string }> {
  cards: T[];
  cursor = 0;
  hasMore: boolean;
  page: number;
  forwardSwipes = 0;
  /** A later page brought no card this deck does not already hold. The server's
   * `has_more` is a ranking-depth guess (and it clamps `deck_page` at
   * DECK_MAX_PAGE), so it can stay true forever at the real end — measured on
   * parikara 2026-10-19: 14–28 identical page requests in 20 s from taps on a
   * chevron that could never advance. Latched until `reset`. */
  exhausted = false;
  private failures = 0;
  private retryAt = 0;
  /** ⛔ KEYED BY CARD ID, NOT BY INDEX. Two live paths shrink the list under an
   * open peek without moving the cursor — `retireForDrift` (quiet by design: it
   * never re-renders an open peek) and `markAdded` retiring a card the peek is
   * NOT showing — so every index above the removed card shifts by one while the
   * visit bookkeeping does not. Under an index key that silently re-attributes a
   * fast swipe to a card the visitor never saw, and reports it as a soft skip. */
  private visited = new Map<string, Visit>();

  constructor(cards: T[], hasMore: boolean, page: number, now = 0) {
    this.cards = cards;
    this.hasMore = hasMore;
    this.page = page;
    this.arrive(now, false);
  }

  /** The id under the cursor, or null when the cursor points at nothing. */
  private idAt(i: number): string | null {
    return this.cards[i]?.id ?? null;
  }

  /** Record arriving at the current cursor. A card already visited is marked
   * `returned` (which disqualifies it as a soft skip) rather than re-clocked. */
  private arrive(now: number, canReturn = true): void {
    const id = this.idAt(this.cursor);
    if (id == null) return;
    const seen = this.visited.get(id);
    if (seen) {
      if (canReturn) seen.returned = true;
      return;
    }
    this.visited.set(id, { arrivedAt: now, leftForwardAfter: null, returned: false });
  }

  /** Record leaving the current cursor in the FORWARD direction. */
  private leaveForward(now: number): void {
    const id = this.idAt(this.cursor);
    if (id == null) return;
    const left = this.visited.get(id);
    if (left) left.leftForwardAfter = now - left.arrivedAt;
  }

  get current(): T | null {
    return this.cards[this.cursor] ?? null;
  }

  /** page 0 replaced (peek closed / open fixture changed): resets cursor and visits */
  reset(cards: T[], hasMore: boolean, now = 0): void {
    this.cards = cards;
    this.hasMore = hasMore;
    this.page = 0;
    this.cursor = 0;
    this.forwardSwipes = 0;
    this.exhausted = false;
    this.failures = 0;
    this.retryAt = 0;
    this.visited.clear();
    this.arrive(now, false);
  }

  /** later page arrived: append only; cursor and visits untouched. Cards the
   * deck already holds are dropped; a page with nothing new ends the deck.
   * Returns how many cards were appended. */
  append(cards: T[], hasMore: boolean, page: number): number {
    const held = new Set(this.cards.map((c) => c.id));
    // ⛔ One card per match: a fact card on a later page never repeats a match the
    // deck already holds (Parikara 2026-10-20: «Вашингтон — Лос Анджелес» had a
    // priced card on page 1 and a fact card on page 2). The server dedupes one page.
    const eventOf = (c: T): string => String((c as { event_id?: unknown }).event_id ?? "");
    const isFact = (c: T): boolean => (c as { type?: unknown }).type === "fact";
    const heldEvents = new Set(this.cards.map(eventOf).filter(Boolean));
    const fresh = cards.filter((c) => {
      if (held.has(c.id)) return false;
      if (isFact(c) && heldEvents.has(eventOf(c))) return false;
      held.add(c.id);
      if (eventOf(c)) heldEvents.add(eventOf(c));
      return true;
    });
    this.cards = this.cards.concat(fresh);
    this.failures = 0;
    this.retryAt = 0;
    if (fresh.length === 0) this.exhausted = true;
    this.hasMore = hasMore && !this.exhausted;
    this.page = page;
    return fresh.length;
  }

  /** The server's `has_more` from a page-0 poll — never reopens an exhausted deck. */
  setHasMore(hasMore: boolean): void {
    this.hasMore = hasMore && !this.exhausted;
  }

  /** A page fetch failed: hold further asks for an exponentially growing wait. */
  noteFetchFailed(now: number): void {
    this.failures += 1;
    this.retryAt = now + Math.min(DECK_RETRY_MAX_MS, DECK_RETRY_BASE_MS * 2 ** (this.failures - 1));
  }

  /** May the next page be requested now (there is more, and no back-off holds)? */
  canAsk(now: number): boolean {
    return this.hasMore && now >= this.retryAt;
  }

  next(now: number): boolean {
    if (this.cursor >= this.cards.length - 1) return false;
    this.leaveForward(now);
    this.cursor += 1;
    this.forwardSwipes += 1;
    this.arrive(now);
    return true;
  }

  prev(now: number): boolean {
    if (this.cursor <= 0) return false;
    this.cursor -= 1;
    this.arrive(now);
    return true;
  }

  goto(i: number, now: number): boolean {
    if (i < 0 || i >= this.cards.length || i === this.cursor) return false;
    const forward = i > this.cursor;
    if (forward) {
      this.leaveForward(now);
      this.forwardSwipes += 1;
    }
    this.cursor = i;
    this.arrive(now);
    return true;
  }

  /** true when the next page should be requested */
  needsMore(): boolean {
    return this.hasMore && this.cursor >= this.cards.length - 3;
  }

  seenIds(): string[] {
    return this.cards.map((c) => c.id);
  }

  /** cards LEFT FORWARD with dwell < SOFT_SKIP_DWELL_MS and never returned to */
  softSkips(): T[] {
    const out: T[] = [];
    const byId = new Map(this.cards.map((c) => [c.id, c]));
    for (const [id, v] of this.visited.entries()) {
      if (v.leftForwardAfter !== null && v.leftForwardAfter < SOFT_SKIP_DWELL_MS && !v.returned) {
        // A card retired out of `cards` mid-peek (drift, or an add elsewhere in
        // the deck) is simply not reported — it was withdrawn, not refused.
        const card = byId.get(id);
        if (card) out.push(card);
      }
    }
    return out;
  }
}
